import * as React from 'react';

import { Button, ConsoleTouchDialog } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { Camera, VideoOff } from 'lucide-react';

import {
  $closeLineItemPhotoModal,
  $lineItemPhotoModalState,
} from '../../../../_state/lineItemPhoto.state';
import { useTranslations } from '../../../../i18n/useTranslations';

import { LoadFormLineItem } from '~/modules/trade/types/load';
import { useTheme, useUIPreferences } from '~/shared/hooks';

type LineItemPhotoModalProps = {
  stagedLineItems: LoadFormLineItem[];
};

export const LineItemPhotoModal: React.FC<LineItemPhotoModalProps> = ({
  stagedLineItems,
}) => {
  const t = useTranslations();
  const { effectiveColorScheme } = useTheme();
  const { touchModeEnabled } = useUIPreferences();

  const modalState = useAtomValue($lineItemPhotoModalState);
  const closeModal = useSetAtom($closeLineItemPhotoModal);

  const lineItem = React.useMemo(
    () => stagedLineItems.find((item) => item.id === modalState.lineItemId),
    [stagedLineItems, modalState.lineItemId],
  );

  const photoUrl = lineItem?.photo?.previewUrl;

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        closeModal();
      }
    },
    [closeModal],
  );

  return (
    <ConsoleTouchDialog
      touchModeEnabled={touchModeEnabled}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={modalState.isOpen}
      onOpenChange={handleOpenChange}
      className="@LineItemPhotoModal"
      dialogClassName="h-[60vh] w-[50vw] max-w-[800px]"
      header={
        <div className="flex items-center gap-2">
          <Camera size={20} />
          <span className="font-medium">{t.loadStaged.viewPhoto}</span>
        </div>
      }
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="outline" onPress={() => closeModal()}>
            {t.loadStaged.close}
          </Button>
        </div>
      }
    >
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="relative aspect-video w-full max-w-[640px] overflow-hidden rounded-lg bg-neutral/10">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Captured line item"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-neutral/50">
              <VideoOff size={32} />
              <span>{t.loadStaged.noPhotoAvailable}</span>
            </div>
          )}
        </div>
      </div>
    </ConsoleTouchDialog>
  );
};
